import { promiseState } from '@passionware/platform-react';
import { X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isImageFromType } from 'lib/media/files';
import { Button } from 'v5/design-system/Button';
import { LoadingSpinner } from 'v5/design-system/LoadingSpinner';
import { EnterpriseTeamIcon } from 'v5/features/core/enterprise/EnterpriseTeamIcon';
import { EnterpriseWorkspaceIcon } from 'v5/features/core/enterprise/EnterpriseWorkspaceIcon';
import { FileUploadDropzone } from 'v5/patterns/forms/fields/FileUploadDropzone';
import { WithServices } from 'v5/platform/ts/services';
import { WithToastService } from 'v5/services/front/ToastService/ToastService';
import { WithUploadService } from 'v5/services/io/UploadService/UploadService';

export type EnterpriseIconUploadFieldProps = WithServices<
  [WithUploadService, WithToastService]
> & {
  value: string;
  onChange: (value: string) => void;
  previewName: string;
  variant: 'workspace' | 'team';
  iconColor?: string;
  disabled?: boolean;
};

export function EnterpriseIconUploadField({
  services,
  value,
  onChange,
  previewName,
  variant,
  iconColor = '',
  disabled = false
}: EnterpriseIconUploadFieldProps) {
  const { t } = useTranslation('core', { keyPrefix: 'enterprise' });
  const { uploadService, toastService } = services;
  const [uploadProgress, setUploadProgress] = useState(0);

  const uploadMutation = promiseState.useMutation(async (file: File) => {
    const result = await uploadService.uploadMedia({
      media: file,
      secure: false,
      onProgress: progress => setUploadProgress(progress)
    });
    onChange(result.accessUrl);
  });

  const handleUploadError = useCallback(() => {
    toastService.open({
      type: 'error',
      message: t('iconUploadError')
    });
  }, [toastService, t]);

  const handleDrop = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!file) {
        return;
      }
      try {
        setUploadProgress(0);
        await uploadMutation.track(file);
      } catch {
        handleUploadError();
      }
    },
    [handleUploadError, uploadMutation]
  );

  const isUploading = uploadMutation.state.status === 'pending';

  const preview =
    variant === 'workspace' ? (
      <EnterpriseWorkspaceIcon
        name={previewName}
        iconUrl={value}
        className="size-12 rounded-lg"
      />
    ) : (
      <EnterpriseTeamIcon
        name={previewName}
        iconUrl={value}
        iconColor={iconColor}
        className="size-12 rounded-lg"
      />
    );

  return (
    <div className="space-y-3">
      {value ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-border-default bg-bg-subtle p-3">
          <div className="flex min-w-0 items-center gap-3">
            {preview}
            <p className="truncate text-sm font-medium text-fg-default">
              {previewName}
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled || isUploading}
            onClick={() => onChange('')}
            className="size-8 shrink-0 p-0"
            aria-label={t('removeIcon')}
          >
            <X className="size-4" />
          </Button>
        </div>
      ) : null}

      <FileUploadDropzone
        onDrop={files => void handleDrop(files)}
        multiple={false}
        accept="image/*"
        maxSize={10 * 1024 * 1024}
        disabled={disabled || isUploading}
        compact
        pasteScope="window"
        title={t('iconUploadHint')}
      />

      {isUploading ? (
        <div className="flex items-center gap-2 rounded-md border border-border-default bg-bg-subtle p-3">
          <LoadingSpinner className="size-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-fg-default">
              {t('uploadIcon')}
            </p>
            <div className="mt-1 h-1.5 w-full rounded-full bg-bg-default">
              <div
                className="h-1.5 rounded-full bg-brand-500 transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
